import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { FormModelControl } from '@bcl/common-ui';
import { SnackbarService, uiDebounce } from '@bcl/design-system';
import { startWith, Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments';
import {
  DocumentsList,
  DocumentsListFilters,
  DocumentsListItem,
} from './visit-documents.model';
import { ParentObject } from 'src/app/shared-components/components/notes-list/notes.model';
import { DocumentsListApiService } from './visit-documents.service';
import { BaseAccordionComponent } from '../base-accordion.component';
import { AccordionService } from 'src/app/services/accordion.service';

const defaultFilter: DocumentsListFilters = {
  searchText: null,
};

@Component({
  selector: 'app-visit-documents',
  templateUrl: './visit-documents.component.html',
  styleUrls: ['./visit-documents.component.scss'],
})
export class VisitDocumentsComponent
  extends BaseAccordionComponent
  implements OnInit, OnDestroy
{
  @Input() public parentObject!: ParentObject;
  @Input() public parentId!: string;
  public documentsList: DocumentsList = {
    attachments: [],
    totalEntries: 0,
  };
  public readonly environment = environment;

  public readonly filterForm: FormGroup<FormModelControl<DocumentsListFilters>>;
  public downloadingDocument = false;

  private pageNumber = 1;
  private pageSize = 10;

  constructor(
    fb: FormBuilder,
    private api: DocumentsListApiService,
    private snackbarService: SnackbarService,
    accordionService: AccordionService
  ) {
    super(accordionService);
    this.filterForm = fb.group(defaultFilter);
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.filterForm.valueChanges
      .pipe(startWith(defaultFilter), uiDebounce(), takeUntil(this.destroyed$))
      .subscribe(() => {
        this.pageNumber = 1;
        this.getDocumentsList();
      });
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
  }
  public loadMore(): void {
    this.pageNumber++;
    this.getDocumentsList();
  }

  public resetFilter(): void {
    this.filterForm.reset(defaultFilter);
  }

  public showDocumentPreview(document: DocumentsListItem): void {
    document.downloading = true;
    this.downloadingDocument = true;

    this.api
      .downloadFile(document.referenceId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (blob) => {
          this.api.openFileInNewTab(blob);
          document.downloading = false;
          this.downloadingDocument = false;
        },
        error: () => {
          this.snackbarService.open(
            'red',
            'DOCUMENTS_LIST.MESSAGES.DOWNLOAD_DOCUMENT_ERROR'
          );
          document.downloading = false;
          this.downloadingDocument = false;
        },
      });
  }

  public downloadDocument(documentItem: DocumentsListItem): void {
    documentItem.downloading = true;
    this.downloadingDocument = true;

    this.api
      .downloadFile(documentItem.referenceId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (blob) => {
          this.api.downloadFileToDisk(blob, documentItem.fileName);

          documentItem.downloading = false;
          this.downloadingDocument = false;
        },
        error: () => {
          this.snackbarService.open(
            'red',
            'DOCUMENTS_LIST.MESSAGES.DOWNLOAD_DOCUMENT_ERROR'
          );
          documentItem.downloading = false;
          this.downloadingDocument = false;
        },
      });
  }
  private getDocumentsList(): void {
    this.api
      .getDocumentsList(
        this.parentObject,
        this.parentId,
        this.pageNumber,
        this.pageSize,
        this.filterForm.value
      )
      .subscribe({
        next: (response) => {
          if (this.pageNumber > 1) {
            this.documentsList = {
              attachments: [
                ...this.documentsList.attachments,
                ...response.attachments,
              ],
              totalEntries: response.totalEntries,
            };
          } else {
            this.documentsList = response;
          }
        },
        error: () => {
          this.snackbarService.open(
            'red',
            'DOCUMENTS_LIST.MESSAGES.LOAD_DOCUMENTS_ERROR'
          );
        },
      });
  }
}
